// Compose a plain-text representation of an activity that teachers can paste
// into email, Google Docs, Word, etc. Handles BOTH the new lesson_markdown
// format AND the legacy structured-field format.

const stripMd = (md) => {
  if (!md) return "";
  return String(md)
    // strip code fences
    .replace(/```[\s\S]*?```/g, (block) => block.replace(/```\w*\n?/g, "").replace(/```$/g, ""))
    // headings: keep text, drop hashes
    .replace(/^#{1,6}\s+/gm, "")
    // bold/italic markers
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "$1")
    .replace(/_(.+?)_/g, "$1")
    // inline code
    .replace(/`(.+?)`/g, "$1")
    // bullet markers
    .replace(/^\s*[-*+]\s+/gm, "• ")
    // horizontal rules
    .replace(/^-{3,}\s*$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const composeFromLegacy = (a) => {
  const lines = [];
  lines.push(a.activity_name || "Untitled Activity");
  if (a.tagline) lines.push(a.tagline);
  const inputs = a.inputs || {};
  const chip = ["grade", "subject", "time", "activity_type", "skill_focus"]
    .filter((k) => inputs[k])
    .map((k) => `${k.replace(/_/g, " ")}: ${inputs[k]}`)
    .join("   ·   ");
  if (chip) lines.push(chip);
  lines.push("");

  if (a.overview) {
    lines.push("OVERVIEW");
    lines.push(a.overview);
    lines.push("");
  }
  if (a.transferable_skills?.length) {
    lines.push("TRANSFERABLE SKILLS");
    lines.push(a.transferable_skills.join("  ·  "));
    lines.push("");
  }
  if (a.ontario_learning_skills?.length) {
    lines.push("ONTARIO LEARNING SKILLS");
    lines.push(a.ontario_learning_skills.join("  ·  "));
    lines.push("");
  }
  if (a.curriculum_links?.length) {
    lines.push("CURRICULUM LINKS");
    a.curriculum_links.forEach((c) => lines.push(`• ${c}`));
    lines.push("");
  }
  if (a.facilitation_moves?.length) {
    lines.push("FACILITATION MOVES");
    a.facilitation_moves.forEach((m, i) => {
      const step = (m && (m.step || m)) || "";
      const tip = (m && m.teacher_tip) || "";
      lines.push(`${i + 1}. ${step}`);
      if (tip) lines.push(`   Teacher tip: ${tip}`);
    });
    lines.push("");
  }
  if (a.teacher_checklist?.length) {
    lines.push("TEACHER CHECKLIST");
    a.teacher_checklist.forEach((c) => lines.push(`• ${c}`));
    lines.push("");
  }
  if (a.observation_prompts?.length) {
    lines.push("OBSERVATION PROMPTS");
    a.observation_prompts.forEach((c) => lines.push(`• ${c}`));
    lines.push("");
  }
  if (a.differentiation_tip) {
    lines.push("DIFFERENTIATION TIP");
    lines.push(a.differentiation_tip);
    lines.push("");
  }
  if (a.extension_idea) {
    lines.push("EXTENSION IDEA");
    lines.push(a.extension_idea);
    lines.push("");
  }
  return lines.join("\n").trim();
};

const composeFromMarkdown = (a) => {
  const lines = [];
  lines.push(a.activity_name || "Untitled Lesson");
  if (a.tagline) lines.push(a.tagline);
  const inputs = a.inputs || {};
  const chip = ["grade", "subject", "time", "activity_type", "skill_focus"]
    .filter((k) => inputs[k])
    .map((k) => `${k.replace(/_/g, " ")}: ${inputs[k]}`)
    .join("   ·   ");
  if (chip) lines.push(chip);
  lines.push("");
  lines.push(stripMd(a.lesson_markdown));
  return lines.join("\n").trim();
};

export const activityToPlainText = (activity) => {
  if (!activity) return "";
  return activity.lesson_markdown
    ? composeFromMarkdown(activity)
    : composeFromLegacy(activity);
};

export const copyActivityToClipboard = async (activity) => {
  const text = activityToPlainText(activity);
  if (!text) throw new Error("Nothing to copy.");
  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    navigator.clipboard.writeText
  ) {
    await navigator.clipboard.writeText(text);
    return text;
  }
  // Fallback for older browsers / insecure contexts
  if (typeof document !== "undefined") {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand && document.execCommand("copy");
    document.body.removeChild(ta);
    if (!ok) throw new Error("Clipboard not available.");
    return text;
  }
  throw new Error("Clipboard not available.");
};
